import { QueryClient, UseBaseQueryOptions, useQuery } from '@tanstack/react-query'
import qs from 'query-string'
import { ResponseType } from 'axios'
import { createApiRequest } from './createApiRequest'

export interface definitionOptions {
    key: ((arg: any) => string[]) | string[]
    url: string
    options?: Partial<UseBaseQueryOptions>
    server?: 'node' | 'python'
}

export interface usageOptions<TData, TError> {
    queryParams?: Record<string, string | string[]>
    pathParams?: Record<string, string>
    enabled?: boolean
    onSuccess?: (data: TData, ...args: Record<string, unknown>[]) => void
    onError?: (error: TError, ...args: Record<string, unknown>[]) => void
    key?: string[]
    refetchInterval?: number
    keepPreviousData?: boolean
    skipErrorToast?: boolean
    apiOptions?: {
        responseType?: ResponseType
        headers?: Record<string, string>
    }
}

type QueryHook<TData, TError> = {
    (usageOptions?: usageOptions<TData, TError>): ReturnType<typeof useQuery<unknown, TError, TData, any>>
    prefetch: (queryClient: QueryClient, usageOptions?: usageOptions<TData, TError>) => Promise<void>
}

export const createQuery = <TData, TError = unknown>(definitionOptions: definitionOptions): QueryHook<TData, TError> => {
    const buildQueryOptions = (usageOptions?: usageOptions<TData, TError>) => {
        if (!usageOptions) {
            usageOptions = {}
        }

        const { url, key = [], server } = definitionOptions
        const { queryParams = {}, pathParams = {}, key: usage_key, apiOptions, skipErrorToast, ...rest } = usageOptions

        const mergeOptions = {
            ...definitionOptions.options,
            ...rest,
        }

        // Interpolate path parameters in URL
        let finalUrl = url
        Object.entries(pathParams).forEach(([param, value]) => {
            finalUrl = finalUrl.replace(`:${param}`, value)
        })

        const params = queryParams ? qs.stringify(queryParams) : ''
        const keyParams = queryParams ? qs.stringify(queryParams) : ''
        const pathParamsString = Object.entries(pathParams)
            .map(([k, v]) => `${k}=${v}`)
            .join('&')
        const allParams = [keyParams, pathParamsString].filter(Boolean).join('&')
        const pathKeySegment = Object.keys(pathParams)
            .sort()
            .map(param => pathParams[param])

        const baseKey = typeof key === 'function' ? key(allParams) : key
        const queryKey = [
            ...(usage_key ?? baseKey),
            ...pathKeySegment,
            ...(keyParams ? [keyParams] : []),
        ]

        return {
            queryKey,
            queryFn: () =>
                createApiRequest<TData>({
                    url: finalUrl,
                    queryParams: params,
                    method: 'GET',
                    server,
                    apiOptions,
                    skipErrorToast,
                }),
            ...mergeOptions,
        }
    }

    const useQueryFn = ((usageOptions?: usageOptions<TData, TError>) => {
        return useQuery<unknown, TError, TData, any>(buildQueryOptions(usageOptions))
    }) as QueryHook<TData, TError>

    useQueryFn.prefetch = async (queryClient, usageOptions) => {
        await queryClient.prefetchQuery(buildQueryOptions(usageOptions))
    }

    return useQueryFn
}
